
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { meApi } from "@/lib/api/me";
import { getMeSession } from "@/lib/auth/storage";
import { formatDateDisplay, todayIST } from "@/lib/timezone";
import { useMeNotices } from "@/hooks/use-me-queries";
import {
  NOTICE_REASONS,
  NOTICE_TYPES,
  type NoticeApprovalStatus,
  type NoticeSession,
  type NoticeType,
} from "@/lib/types";

const SESSIONS: { value: NoticeSession; label: string }[] = [
  { value: "morning", label: "Morning" },
  { value: "morning_block", label: "Morning block (9–1)" },
  { value: "evening_block", label: "Evening block (5–9)" },
  { value: "whole_day", label: "Whole day" },
];

const APPROVAL_LABELS: Record<NoticeApprovalStatus, string> = {
  submitted: "Pending review",
  approved: "Approved — marked O",
  rejected: "Rejected — marked A",
};

const APPROVAL_VARIANT: Record<
  NoticeApprovalStatus,
  "default" | "secondary" | "outline"
> = {
  submitted: "default",
  approved: "secondary",
  rejected: "outline",
};

export default function MeNoticePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const meSession = getMeSession();
  const { data: notices, isLoading: noticesLoading } = useMeNotices();
  const [date, setDate] = useState(todayIST());
  const [session, setSession] = useState<NoticeSession>("morning");
  const [noticeType, setNoticeType] = useState<NoticeType>("sick");
  const [reason, setReason] = useState<string>(NOTICE_REASONS[0]);
  const [notes, setNotes] = useState("");

  const submit = useMutation({
    mutationFn: () =>
      meApi.submitNotice(meSession!, {
        date,
        session,
        notice_type: noticeType,
        reason,
        notes: notes.trim() || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["me", "notices"] });
      setNotes("");
    },
  });

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold">Notices</h2>
        <p className="text-sm text-muted-foreground">
          Submit an absence notice. Your coordinator will approve (O) or reject
          (A).
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Your notices</CardTitle>
          <CardDescription>Status updates when admin reviews</CardDescription>
        </CardHeader>
        <CardContent>
          {noticesLoading && (
            <p className="text-sm text-muted-foreground">Loading…</p>
          )}
          {!noticesLoading && (notices?.length ?? 0) === 0 && (
            <p className="text-sm text-muted-foreground">
              No notices submitted yet.
            </p>
          )}
          <ul className="space-y-3">
            {notices?.map((notice) => (
              <li
                key={notice.id}
                className="rounded-lg border border-saffron-100 p-3 text-sm"
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="font-medium">{formatDateDisplay(notice.date)}</p>
                  <Badge variant={APPROVAL_VARIANT[notice.approval_status]}>
                    {APPROVAL_LABELS[notice.approval_status]}
                  </Badge>
                </div>
                <p className="mt-1 text-muted-foreground">
                  {notice.session.replace(/_/g, " ")} · {notice.reason}
                </p>
                {notice.reviewed_at && notice.approval_status !== "submitted" && (
                  <p className="mt-1 text-xs text-muted-foreground">
                    Reviewed{" "}
                    {formatDateDisplay(notice.reviewed_at.slice(0, 10))}
                  </p>
                )}
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Submit new notice</CardTitle>
        </CardHeader>
        <CardContent>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              submit.mutate();
            }}
            className="space-y-4"
          >
            <div className="space-y-2">
              <Label htmlFor="date">Date</Label>
              <Input
                id="date"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <Label>Session</Label>
              <Select
                value={session}
                onValueChange={(v) => setSession(v as NoticeSession)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SESSIONS.map((s) => (
                    <SelectItem key={s.value} value={s.value}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Type</Label>
              <Select
                value={noticeType}
                onValueChange={(v) => setNoticeType(v as NoticeType)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {NOTICE_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Reason</Label>
              <Select value={reason} onValueChange={setReason}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {NOTICE_REASONS.map((r) => (
                    <SelectItem key={r} value={r}>
                      {r}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Notes (optional)</Label>
              <Textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Any extra details…"
              />
            </div>

            {submit.isError && (
              <p className="text-sm text-red-600">Could not submit notice.</p>
            )}
            {submit.isSuccess && (
              <p className="text-sm text-emerald-700">
                Notice submitted — awaiting coordinator review.
              </p>
            )}

            <div className="flex gap-2">
              <Button
                type="submit"
                className="flex-1"
                size="lg"
                disabled={submit.isPending}
              >
                {submit.isPending ? "Submitting…" : "Submit notice"}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="lg"
                onClick={() => navigate("/me")}
              >
                Done
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
